import style from "@/styles/pages/SelectAssessmentType/SelectAssessmentType.module.css";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import ExistingMemberCard from "@/components/GetStarted/ExistingMemberCard";
import { setApplicationProgress, setPurchaseTypeSelected, setSearchingForProperty } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import clsx from "clsx";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors, setSingleClickInsightEvent } from "@/lib/util";

export default function SelectAssessmentType() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [assessmentType, setAssessmentType] = useState(0);
    const [displayError, setDisplayError] = useState(false);

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const homeAddress = useSelector(
            (state: RootState) => state.homeAddress.address
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            assessmentType: "",
        },
    });

    useEffect(() => {
        document.title = "Select Home Buying Process";
        if (application.purchaseTypeSelected){
            if (!application.searchingForProperty) {
                setAssessmentType(1);
            } else {
                setAssessmentType(2);
            }
        }

        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        client.apiApplicationsFetch().then((result) => {
            console.log(result);
            result.application = application;
            result.homeAddress = homeAddress;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }, []);


    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        if (assessmentType === 0) {
            setDisplayError(true);
            return;
        }
        switch (assessmentType) {
            case 1:
                dispatch(setSearchingForProperty(false));
                break;
            case 2:
                dispatch(setSearchingForProperty(true));
                break;
        }
        dispatch(setPurchaseTypeSelected(true));
        dispatch(setApplicationProgress(2));
        setClickInsightApplicationProgress('Your new mortgage - Where are you in the home buying process?');
        navigate("/single/purchase-price");
    };

    const handleClick = (type: number) => {
        setAssessmentType(type);
        setDisplayError(false);
        setSingleClickInsightEvent('buyingStageSelected', undefined, type == 1 ? "I've found my new home" : "I'm searching for my dream home");
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-3">
                            <h1 aria-label="Address Header">Where are you in the home buying process?</h1>
                            <p className="sub-heading">Help us understand what you need.</p>
                        </div>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    Please select an option to continue.
                                </span>
                            </div>
                        }
                        <div className={clsx("flex flex-row", displayError ? "" : "mt-5")}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="content-container">
                                        <div className="grid grid-cols-2 md:grid-cols-3">
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(1)}>
                                                    <ExistingMemberCard
                                                        cardHeight={240}
                                                        cardWidth={215}
                                                        imageSrc="assets/mortgage-approval.svg"
                                                        cardText="I've found my new home"
                                                        secondaryText="Mortgage approval"
                                                        selected={assessmentType == 1}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(2)}>
                                                    <ExistingMemberCard
                                                        cardHeight={240}
                                                        cardWidth={215}
                                                        imageSrc="assets/mortgage-preapproval.svg"
                                                        cardText="I'm searching for my dream home"
                                                        secondaryText="Mortgage pre-approval"
                                                        selected={assessmentType == 2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="sub-heading" style={{ fontSize: 14 }}><b>Note: </b>You don't need to complete a pre-approval application to start a mortgage application.</p>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate("/single/select-application")}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate("/single/select-application")}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}